import { RootState } from '@/app/redux/store';
import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ParsedToken } from 'firebase/auth';

type SessionState = {
  authInitialized: boolean;
} & (
  | {
      isAuthenticated: false;
    }
  | {
      isAuthenticated: true;
      uid: string;
      email: string;
      isEmailVerified: boolean;
      claims: ParsedToken | null;
    }
);

const sessionSlice = createSlice({
  name: 'session',
  initialState: {
    authInitialized: false,
    isAuthenticated: false,
  } as SessionState,
  reducers: {
    authenticateUser(
      state,
      action: PayloadAction<{
        uid: string;
        email: string;
        isEmailVerified: boolean;
      }>,
    ) {
      const { uid, email, isEmailVerified } = action.payload;
      return {
        ...state,
        authInitialized: true,
        isAuthenticated: true,
        uid,
        email,
        isEmailVerified,
        claims: null,
        pendingInvite: null,
      };
    },
    unauthenticateUser() {
      return { authInitialized: true, isAuthenticated: false };
    },

    setIsEmailVerified(state, action: PayloadAction<boolean>) {
      return {
        ...state,
        isEmailVerified: action.payload,
      };
    },

    setClaims(state, action: PayloadAction<ParsedToken | null>) {
      return {
        ...state,
        claims: action.payload,
      };
    },
  },
});

export default sessionSlice.reducer;

export const {
  authenticateUser,
  unauthenticateUser,
  setIsEmailVerified,
  setClaims,
} = sessionSlice.actions;

export const selectAuthInitialized = (state: RootState) =>
  state.session.authInitialized;

export const selectIsAuthenticated = (state: RootState) =>
  state.session.isAuthenticated;

export const selectUserId = (state: RootState) => {
  const session = state.session;
  if (!session.isAuthenticated) return null;
  return session.uid;
};

export const selectIsEmailVerified = (state: RootState) => {
  const session = state.session;
  if (!session.isAuthenticated) return null;
  return session.isEmailVerified;
};

export const selectSessionUserDetails = createSelector(
  (state: RootState) => state.session,
  (session) => {
    if (!session.isAuthenticated) return null;

    return {
      uid: session.uid,
      email: session.email,
      isEmailVerified: session.isEmailVerified,
    };
  },
);

export const requireSessionUserDetails = (state: RootState) => {
  const details = selectSessionUserDetails(state);
  if (!details) throw new Error('User is not authenticated');
  return details;
};

export const selectClaims = (state: RootState) => {
  const session = state.session;
  if (!session.isAuthenticated) return null;
  return session.claims;
};
